@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap');

:root {
  font-family: 'Space Grotesk', ui-sans-serif, system-ui, -apple-system, sans-serif;
  font-synthesis: none;
  text-rendering: optimizeLegibility;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

html {
  scroll-behavior: smooth;
}

body {
  min-width: 320px;
  min-height: 100vh;
  background: #080a0e;
  color: #e2e8f3;
  overflow-x: hidden;
}

button,
a {
  font: inherit;
  cursor: pointer;
}

a {
  color: inherit;
  text-decoration: none;
}

/* ── Design tokens ────────────────────────────────── */
.site {
  --bg:           #080a0e;
  --surface:      #0d1018;
  --surface-2:    #131720;
  --surface-3:    #1a2030;
  --ink:          #e2e8f3;
  --muted:        #8896aa;
  --faint:        #3d4a5c;
  --line:         #1e2738;
  --green:        #00e5a0;
  --green-dim:    #00c48a;
  --green-glow:   rgba(0, 229, 160, 0.12);
  --purple:       #9b87f5;
  --purple-dim:   #7b6ef6;
  --purple-glow:  rgba(155, 135, 245, 0.12);
  --amber:        #f5c842;
  --red:          #ff5e7d;
  --radius:       14px;
  --radius-lg:    20px;
  --radius-xl:    28px;
  min-height: 100vh;
  background: var(--bg);
  color: var(--ink);
  overflow-x: hidden;
}

/* ── Nav ──────────────────────────────────────────── */
.nav {
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  gap: 32px;
  width: min(1160px, calc(100% - 32px));
  margin: 0 auto;
  padding: 20px 0;
  background: color-mix(in srgb, var(--bg) 80%, transparent);
  backdrop-filter: blur(20px);
}

.nav-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1.1rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  flex-shrink: 0;
}

.nav-brand-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  background: linear-gradient(135deg, var(--green) 0%, var(--purple) 100%);
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 700;
  color: #080a0e;
  font-family: 'JetBrains Mono', monospace;
  letter-spacing: 0;
}

.nav-links {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
}

.nav-links a {
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--muted);
  transition: color 0.15s, background 0.15s;
}

.nav-links a:hover {
  color: var(--ink);
  background: var(--surface-2);
}

.nav-cta {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 20px;
  border-radius: 999px;
  background: var(--green);
  color: #080a0e;
  font-size: 0.875rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  transition: background 0.15s, transform 0.1s;
  flex-shrink: 0;
}

.nav-cta:hover {
  background: var(--green-dim);
  transform: translateY(-1px);
}

/* ── Product switcher ─────────────────────────────── */
.nav-products {
  display: flex;
  gap: 2px;
  padding: 3px;
  background: var(--surface-2);
  border: 1px solid var(--line);
  border-radius: 999px;
  flex-shrink: 0;
}

.nav-product {
  padding: 5px 14px;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 500;
  font-family: 'JetBrains Mono', monospace;
  color: var(--muted);
  transition: color 0.15s, background 0.15s;
}

.nav-product:hover {
  color: var(--ink);
}

.nav-product.is-active {
  color: var(--bg);
  background: linear-gradient(135deg, var(--green) 0%, var(--purple) 100%);
  font-weight: 600;
}

@media (max-width: 720px) {
  .nav-links {
    display: none;
  }
}

/* ── Layout helpers ───────────────────────────────── */
.wrap {
  width: min(1160px, calc(100% - 32px));
  margin: 0 auto;
}

.section {
  padding: 96px 0;
}

.section-label {
  display: inline-block;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--green);
  margin-bottom: 16px;
}

.section-title {
  font-size: clamp(1.9rem, 4vw, 2.8rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.15;
  color: var(--ink);
  margin-bottom: 20px;
}

.section-sub {
  font-size: 1.05rem;
  line-height: 1.7;
  color: var(--muted);
  max-width: 52ch;
}

/* ── Hero ─────────────────────────────────────────── */
.hero {
  padding: 80px 0 96px;
  position: relative;
  overflow: hidden;
}

.hero::before {
  content: '';
  position: absolute;
  top: -200px;
  left: 50%;
  transform: translateX(-50%);
  width: 900px;
  height: 600px;
  background: radial-gradient(ellipse at center, rgba(0,229,160,0.08) 0%, rgba(155,135,245,0.05) 50%, transparent 70%);
  pointer-events: none;
}

.hero-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  border-radius: 999px;
  border: 1px solid var(--line);
  background: var(--surface);
  font-size: 0.82rem;
  font-weight: 500;
  color: var(--muted);
  margin-bottom: 32px;
  font-family: 'JetBrains Mono', monospace;
}

.hero-eyebrow-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--green);
  box-shadow: 0 0 8px var(--green);
}

.hero-title {
  font-size: clamp(2.8rem, 7vw, 5.2rem);
  font-weight: 700;
  letter-spacing: -0.03em;
  line-height: 1.05;
  margin-bottom: 28px;
  max-width: 14ch;
}

.hero-title em {
  font-style: normal;
  background: linear-gradient(135deg, var(--green) 0%, var(--purple) 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.hero-lead {
  font-size: 1.15rem;
  line-height: 1.75;
  color: var(--muted);
  max-width: 54ch;
  margin-bottom: 44px;
  overflow-wrap: break-word;
}

.hero-actions {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 56px;
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 28px;
  border-radius: 999px;
  background: var(--green);
  color: #080a0e;
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  border: none;
  transition: background 0.15s, transform 0.1s, box-shadow 0.15s;
}

.btn-primary:hover {
  background: var(--green-dim);
  transform: translateY(-2px);
  box-shadow: 0 8px 32px rgba(0,229,160,0.28);
}

.btn-ghost {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 28px;
  border-radius: 999px;
  background: transparent;
  color: var(--muted);
  font-size: 0.95rem;
  font-weight: 500;
  border: 1px solid var(--line);
  transition: color 0.15s, border-color 0.15s, transform 0.1s;
}

.btn-ghost:hover {
  color: var(--ink);
  border-color: var(--faint);
  transform: translateY(-1px);
}

.hero-install {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  border-radius: var(--radius);
  background: var(--surface);
  border: 1px solid var(--line);
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.9rem;
  color: var(--green);
  max-width: 100%;
}

.hero-install-prompt {
  color: var(--faint);
  user-select: none;
}

.hero-install-copy {
  margin-left: auto;
  padding: 4px 10px;
  border-radius: 6px;
  background: var(--surface-2);
  border: 1px solid var(--line);
  font-size: 0.75rem;
  font-family: 'Space Grotesk', sans-serif;
  color: var(--muted);
  transition: color 0.15s, background 0.15s;
}

.hero-install span:nth-child(2) {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.hero-install-copy:hover {
  color: var(--ink);
  background: var(--surface-3);
}

.hero-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px;
  align-items: center;
}

.hero-grid > * {
  min-width: 0;
}

@media (max-width: 860px) {
  .hero-grid {
    grid-template-columns: 1fr;
    gap: 48px;
  }
}

@media (max-width: 560px) {
  .wrap,
  .hero-grid > div,
  .datagrid-preview {
    width: min(358px, calc(100vw - 48px));
    max-width: min(358px, calc(100vw - 48px));
  }

  .hero-lead {
    max-width: min(358px, calc(100vw - 48px));
  }

  .dg-search-bar {
    overflow-x: auto;
  }

  .dg-search-pill {
    flex: 0 0 auto;
  }
}

/* ── Fake datagrid visual ─────────────────────────── */
.datagrid-preview {
  width: 100%;
  max-width: 100%;
  border-radius: var(--radius-xl);
  border: 1px solid var(--line);
  background: var(--surface);
  overflow: hidden;
  box-shadow: 0 32px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(0,229,160,0.05);
  position: relative;
}

.datagrid-preview::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: var(--radius-xl);
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.04);
  pointer-events: none;
}

.dg-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--line);
  background: var(--surface-2);
}

.dg-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.dg-dot-r { background: #ff5e7d; }
.dg-dot-y { background: #f5c842; }
.dg-dot-g { background: #00e5a0; }

.dg-tab-row {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 16px;
  border-bottom: 1px solid var(--line);
  background: var(--surface);
}

.dg-tab {
  padding: 10px 16px;
  font-size: 0.78rem;
  font-weight: 500;
  color: var(--muted);
  border-bottom: 2px solid transparent;
}

.dg-tab.is-active {
  color: var(--green);
  border-bottom-color: var(--green);
}

.dg-search-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  border-bottom: 1px solid var(--line);
  background: var(--surface);
}

.dg-search-pill {
  padding: 4px 12px;
  border-radius: 999px;
  background: var(--green-glow);
  border: 1px solid rgba(0,229,160,0.25);
  font-size: 0.75rem;
  color: var(--green);
  font-family: 'JetBrains Mono', monospace;
}

.dg-search-pill.purple {
  background: var(--purple-glow);
  border-color: rgba(155,135,245,0.25);
  color: var(--purple);
}

.dg-search-pill.amber {
  background: rgba(245,200,66,0.08);
  border-color: rgba(245,200,66,0.25);
  color: var(--amber);
}

.dg-header-row {
  display: grid;
  grid-template-columns: 1.4fr 1fr 0.8fr 1fr 0.9fr;
  gap: 0;
  padding: 0 16px;
  background: var(--surface-2);
  border-bottom: 1px solid var(--line);
}

.dg-th {
  padding: 8px 0;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--faint);
}

.dg-row {
  display: grid;
  grid-template-columns: 1.4fr 1fr 0.8fr 1fr 0.9fr;
  gap: 0;
  padding: 0 16px;
  border-bottom: 1px solid var(--line);
  transition: background 0.1s;
}

.dg-row:last-child {
  border-bottom: none;
}

.dg-row.is-selected {
  background: rgba(0,229,160,0.04);
}

.dg-row:hover {
  background: var(--surface-2);
}

.dg-td {
  padding: 10px 0;
  font-size: 0.8rem;
  color: var(--muted);
  display: flex;
  align-items: center;
}

.dg-td.name {
  color: var(--ink);
  font-weight: 500;
}

.dg-badge {
  display: inline-flex;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
}

.dg-badge-green {
  background: rgba(0,229,160,0.1);
  color: var(--green);
}

.dg-badge-amber {
  background: rgba(245,200,66,0.1);
  color: var(--amber);
}

.dg-badge-red {
  background: rgba(255,94,125,0.1);
  color: var(--red);
}

.dg-pulse {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--green);
  box-shadow: 0 0 6px var(--green);
  animation: pulse 2s ease-in-out infinite;
  flex-shrink: 0;
  margin-right: 6px;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}

.dg-cmd-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80%;
  max-width: 320px;
  background: var(--surface-3);
  border: 1px solid var(--line);
  border-radius: var(--radius-lg);
  box-shadow: 0 24px 60px rgba(0,0,0,0.7);
  padding: 12px;
  display: none;
}

.dg-live-grid {
  --color-primary: var(--green);
  --color-primary-light: var(--green);
  --color-primary-dark: var(--green-dim);
  --color-secondary: var(--purple);
  --color-accent: var(--purple);
  --color-accent-light: rgba(0, 229, 160, 0.04);
  --color-accent-dark: var(--purple);
  --color-background: var(--surface);
  --color-background-light: var(--surface-2);
  --color-background-dark: var(--surface-3);
  --color-border-light: var(--line);
  --color-border-dark: var(--line);
  --color-text: var(--muted);
  --color-text-light: var(--faint);
  --color-text-dark: var(--ink);
  --color-muted-dark: var(--muted);
  --table-scrollbar-track: var(--surface);
  --table-scrollbar-thumb: var(--faint);
  --table-scrollbar-thumb-hover: var(--muted);
  --table-scrollbar-border: var(--line);
  background: var(--surface);
  color: var(--muted);
  font-family: 'Space Grotesk', ui-sans-serif, system-ui, -apple-system, sans-serif;
  min-width: 0;
  overflow: hidden;
  width: 100%;
}

.dg-live-grid .relative { position: relative; }
.dg-live-grid .absolute { position: absolute; }
.dg-live-grid .flex { display: flex; }
.dg-live-grid .flex-col { flex-direction: column; }
.dg-live-grid .flex-row { flex-direction: row; }
.dg-live-grid .items-center { align-items: center; }
.dg-live-grid .justify-between { justify-content: space-between; }
.dg-live-grid .flex-1 { flex: 1 1 0%; }
.dg-live-grid .flex-shrink-0 { flex-shrink: 0; }
.dg-live-grid .min-w-0 { min-width: 0; }
.dg-live-grid .min-h-0 { min-height: 0; }
.dg-live-grid .w-full { width: 100%; }
.dg-live-grid .h-full { height: 100%; }
.dg-live-grid .w-5 { width: 0.82rem; }
.dg-live-grid .h-5 { height: 0.82rem; }
.dg-live-grid .min-w-full { min-width: 100%; }
.dg-live-grid .shrink-0 { flex-shrink: 0; }
.dg-live-grid .ml-2 { margin-left: 0.34rem; }
.dg-live-grid .mr-1 { margin-right: 0.2rem; }
.dg-live-grid .space-x-1 > :not([hidden]) ~ :not([hidden]) { margin-left: 0.18rem; }
.dg-live-grid .truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.dg-live-grid .select-text { user-select: text; }
.dg-live-grid .font-medium { font-weight: 500; }
.dg-live-grid .text-left { text-align: left; }
.dg-live-grid .text-right { text-align: right; }
.dg-live-grid .text-center { text-align: center; }
.dg-live-grid .font-mono { font-family: 'JetBrains Mono', monospace; }
.dg-live-grid .bg-background { background: var(--color-background); }
.dg-live-grid .text-text { color: var(--color-text); }
.dg-live-grid .text-primary { color: var(--color-primary); }
.dg-live-grid .text-muted-dark { color: var(--color-muted-dark); }
.dg-live-grid .cursor-pointer { cursor: pointer; }
.dg-live-grid .transition-colors { transition: color 0.15s ease; }
.dg-live-grid .hover\:text-muted-light:hover { color: var(--ink); }

.dg-live-grid [data-testid='datagrid-root'] {
  height: auto !important;
  max-height: none !important;
  overflow: visible !important;
}

.dg-live-grid [data-testid='datagrid-table-scroll-region'] {
  background: var(--surface);
  border: 0;
  border-radius: 0;
  overflow-x: auto !important;
  overflow-y: hidden !important;
  scrollbar-gutter: auto !important;
}

.dg-live-grid [data-testid='datagrid-table-scroll-region']::-webkit-scrollbar {
  height: 8px;
}

.dg-live-grid [data-testid='datagrid-table-scroll-region']::-webkit-scrollbar-track {
  background: var(--surface);
}

.dg-live-grid [data-testid='datagrid-table-scroll-region']::-webkit-scrollbar-thumb {
  background: var(--faint);
  border-radius: 999px;
}

.dg-live-grid table {
  width: 100% !important;
  min-width: 528px;
  border-collapse: collapse;
  table-layout: fixed;
  background: var(--surface);
}

.dg-live-grid thead {
  background: var(--surface-2);
}

.dg-live-grid th {
  height: 35px;
  background: var(--surface-2) !important;
  border-top: 0 !important;
  border-left: 0 !important;
  border-right: 0 !important;
  border-color: var(--line) !important;
  overflow: hidden;
}

.dg-live-grid .datagrid-column-header-content {
  min-height: 35px;
  display: flex;
  align-items: center;
  padding: 0;
}

.dg-live-grid th:first-child .datagrid-column-header-content {
  padding-left: 16px;
}

.dg-live-grid .datagrid-column-header-content > .flex {
  width: 100%;
}

.dg-live-grid .datagrid-column-header-content span {
  color: var(--faint) !important;
}

.dg-live-grid .datagrid-column-header-content svg {
  color: var(--faint);
  opacity: 0.55;
}

.dg-live-grid .datagrid-column-header-content svg:hover,
.dg-live-grid .datagrid-column-header-content .text-primary svg {
  color: var(--green);
  opacity: 0.95;
}

.dg-live-grid tbody {
  background: var(--surface);
}

.dg-live-grid tbody tr {
  background: var(--surface);
  transition: background 0.1s;
}

.dg-live-grid tbody tr:hover td,
.dg-live-grid tbody tr:hover .datagrid-cell-content {
  background: var(--surface-2) !important;
}

.dg-live-grid tbody tr.is-selected td,
.dg-live-grid tbody tr.is-selected .datagrid-cell-content {
  background: rgba(0, 229, 160, 0.04) !important;
}

.dg-live-grid td {
  height: 40px;
  border-top: 0 !important;
  border-bottom: 1px solid var(--line) !important;
  border-right: 0 !important;
  overflow: hidden;
}

.dg-live-grid tbody tr:last-child td {
  border-bottom: 0 !important;
}

.dg-live-grid .datagrid-cell-content {
  display: flex !important;
  align-items: center;
  min-height: 39px;
  width: 100%;
  padding: 0;
  overflow: hidden;
  color: var(--muted) !important;
  background: var(--surface) !important;
}

.dg-live-grid td:first-child .datagrid-cell-content {
  padding-left: 16px;
}

.dg-live-name {
  color: var(--ink);
  font-weight: 500;
}

.dg-live-mono {
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.78rem;
}

.dg-live-updated {
  display: inline-flex;
  align-items: center;
  min-width: 0;
}

/* ── datasynth demo ───────────────────────────────── */
.ds-demo-controls {
  padding: 18px 16px;
  border-bottom: 1px solid var(--line);
  background: var(--surface);
}

.ds-gen-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 12px 18px;
  border-radius: var(--radius);
  border: none;
  background: var(--green);
  color: #080a0e;
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  transition: background 0.15s, transform 0.1s, opacity 0.15s;
}

.ds-gen-btn:hover:not(:disabled) {
  background: var(--green-dim);
  transform: translateY(-1px);
}

.ds-gen-btn:disabled {
  opacity: 0.7;
  cursor: progress;
}

.ds-spinner {
  width: 13px;
  height: 13px;
  border-radius: 50%;
  border: 2px solid rgba(8, 10, 14, 0.35);
  border-top-color: #080a0e;
  animation: ds-spin 0.7s linear infinite;
}

@keyframes ds-spin {
  to { transform: rotate(360deg); }
}

.ds-status {
  margin-top: 12px;
  font-size: 0.74rem;
  font-family: 'JetBrains Mono', monospace;
  color: var(--faint);
}

.ds-status.is-busy { color: var(--amber); }
.ds-status.is-done { color: var(--green); }

.ds-progress {
  margin-top: 8px;
  height: 4px;
  border-radius: 999px;
  background: var(--surface-3);
  overflow: hidden;
}

.ds-progress-fill {
  display: block;
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, var(--green) 0%, var(--purple) 100%);
  transition: width 0.6s ease;
}

.ds-score-row {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 1px;
  background: var(--line);
  border-bottom: 1px solid var(--line);
}

.ds-score-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 12px 6px;
  background: var(--surface);
  text-align: center;
}

.ds-score-card.overall {
  background: var(--surface-2);
}

.ds-score-label {
  font-size: 0.6rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--faint);
}

.ds-score-value {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--ink);
  font-family: 'JetBrains Mono', monospace;
}

.ds-score-value.green { color: var(--green); }
.ds-score-value.amber { color: var(--amber); }

.ds-demo-foot {
  padding: 10px 16px;
  font-size: 0.72rem;
  color: var(--faint);
  background: var(--surface-2);
  text-align: center;
}

@media (max-width: 560px) {
  .ds-score-label { font-size: 0.52rem; }
  .ds-score-value { font-size: 0.9rem; }
}

/* ── Stats bar ────────────────────────────────────── */
.stats-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1px;
  background: var(--line);
  border: 1px solid var(--line);
  border-radius: var(--radius-lg);
  overflow: hidden;
  margin: 64px 0;
}

@media (max-width: 700px) {
  .stats-row {
    grid-template-columns: repeat(2, 1fr);
  }
}

.stat-cell {
  padding: 32px 28px;
  background: var(--surface);
  text-align: center;
}

.stat-number {
  font-size: 2.4rem;
  font-weight: 700;
  letter-spacing: -0.03em;
  background: linear-gradient(135deg, var(--ink) 0%, var(--muted) 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 6px;
}

.stat-label {
  font-size: 0.82rem;
  color: var(--muted);
}

/* ── Features ─────────────────────────────────────── */
.features-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1px;
  background: var(--line);
  border: 1px solid var(--line);
  border-radius: var(--radius-lg);
  overflow: hidden;
}

@media (max-width: 900px) {
  .features-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 560px) {
  .features-grid {
    grid-template-columns: 1fr;
  }
}

.feature-cell {
  padding: 32px 28px;
  background: var(--surface);
  transition: background 0.15s;
}

.feature-cell:hover {
  background: var(--surface-2);
}

.feature-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  font-size: 1.3rem;
  margin-bottom: 18px;
}

.feature-icon.green {
  background: var(--green-glow);
  border: 1px solid rgba(0,229,160,0.2);
}

.feature-icon.purple {
  background: var(--purple-glow);
  border: 1px solid rgba(155,135,245,0.2);
}

.feature-icon.amber {
  background: rgba(245,200,66,0.08);
  border: 1px solid rgba(245,200,66,0.2);
}

.feature-icon.red {
  background: rgba(255,94,125,0.08);
  border: 1px solid rgba(255,94,125,0.2);
}

.feature-name {
  font-size: 1rem;
  font-weight: 600;
  color: var(--ink);
  margin-bottom: 8px;
}

.feature-desc {
  font-size: 0.875rem;
  line-height: 1.65;
  color: var(--muted);
}

/* ── Code showcase ────────────────────────────────── */
.code-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;
  align-items: start;
}

@media (max-width: 860px) {
  .code-layout {
    grid-template-columns: 1fr;
  }
}

.code-block {
  border-radius: var(--radius-lg);
  background: var(--surface);
  border: 1px solid var(--line);
  overflow: hidden;
}

.code-block-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--line);
  background: var(--surface-2);
}

.code-block-filename {
  font-size: 0.78rem;
  font-family: 'JetBrains Mono', monospace;
  color: var(--muted);
  flex: 1;
}

.code-block-lang {
  font-size: 0.72rem;
  padding: 2px 8px;
  border-radius: 4px;
  background: var(--surface-3);
  color: var(--faint);
  font-family: 'JetBrains Mono', monospace;
}

.code-block pre {
  padding: 20px 20px;
  overflow-x: auto;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.82rem;
  line-height: 1.7;
  color: var(--muted);
}

.code-block pre .kw { color: var(--purple); }
.code-block pre .fn { color: var(--green); }
.code-block pre .str { color: var(--amber); }
.code-block pre .cm { color: var(--faint); }
.code-block pre .tag { color: var(--red); }
.code-block pre .attr { color: var(--purple); }
.code-block pre .val { color: var(--amber); }
.code-block pre .ink { color: var(--ink); }

/* ── Install tabs ─────────────────────────────────── */
.install-tabs {
  display: flex;
  gap: 4px;
  padding: 4px;
  background: var(--surface-2);
  border-radius: 10px;
  margin-bottom: 16px;
  width: fit-content;
}

.install-tab {
  padding: 6px 16px;
  border-radius: 7px;
  font-size: 0.82rem;
  font-weight: 500;
  color: var(--muted);
  background: transparent;
  border: none;
  transition: color 0.15s, background 0.15s;
}

.install-tab.is-active {
  color: var(--ink);
  background: var(--surface-3);
}

.install-tab:hover:not(.is-active) {
  color: var(--ink);
}

/* ── Showcase deep-dives ──────────────────────────── */
.showcase-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

@media (max-width: 800px) {
  .showcase-grid {
    grid-template-columns: 1fr;
  }
}

.showcase-card {
  border-radius: var(--radius-xl);
  background: var(--surface);
  border: 1px solid var(--line);
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  transition: border-color 0.2s, background 0.2s;
}

.showcase-card:hover {
  border-color: var(--faint);
  background: var(--surface-2);
}

.showcase-card.featured {
  grid-column: 1 / -1;
  flex-direction: row;
  gap: 48px;
  align-items: center;
}

@media (max-width: 800px) {
  .showcase-card.featured {
    grid-column: 1;
    flex-direction: column;
  }
}

.showcase-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  background: var(--green-glow);
  border: 1px solid rgba(0,229,160,0.2);
  color: var(--green);
  width: fit-content;
}

.showcase-badge.purple {
  background: var(--purple-glow);
  border-color: rgba(155,135,245,0.2);
  color: var(--purple);
}

.showcase-title {
  font-size: 1.3rem;
  font-weight: 600;
  color: var(--ink);
  letter-spacing: -0.01em;
}

.showcase-body {
  font-size: 0.9rem;
  line-height: 1.7;
  color: var(--muted);
  flex: 1;
}

.showcase-mini-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.showcase-mini-cell {
  padding: 12px;
  border-radius: 10px;
  background: var(--surface-2);
  border: 1px solid var(--line);
}

.showcase-mini-cell strong {
  display: block;
  font-size: 0.78rem;
  color: var(--green);
  margin-bottom: 4px;
  font-family: 'JetBrains Mono', monospace;
}

.showcase-mini-cell span {
  font-size: 0.75rem;
  color: var(--muted);
}

/* ── Pricing ──────────────────────────────────────── */
.pricing-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  align-items: start;
}

@media (max-width: 840px) {
  .pricing-grid {
    grid-template-columns: 1fr;
    max-width: 420px;
  }
}

.pricing-card {
  border-radius: var(--radius-xl);
  background: var(--surface);
  border: 1px solid var(--line);
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 0;
  position: relative;
}

.pricing-card.featured {
  border-color: var(--green);
  background: linear-gradient(180deg, rgba(0,229,160,0.04) 0%, var(--surface) 60%);
  box-shadow: 0 0 0 1px rgba(0,229,160,0.12), 0 16px 48px rgba(0,229,160,0.08);
}

.pricing-featured-tag {
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  padding: 4px 14px;
  border-radius: 999px;
  background: var(--green);
  color: #080a0e;
  font-size: 0.75rem;
  font-weight: 700;
  white-space: nowrap;
}

.pricing-tier {
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--muted);
  margin-bottom: 12px;
}

.pricing-price {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  margin-bottom: 8px;
}

.pricing-currency {
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--muted);
  margin-top: 6px;
}

.pricing-amount {
  font-size: 3.2rem;
  font-weight: 700;
  letter-spacing: -0.03em;
  color: var(--ink);
  line-height: 1;
}

.pricing-period {
  font-size: 0.85rem;
  color: var(--muted);
  margin-top: 4px;
  align-self: flex-end;
  margin-bottom: 2px;
}

.pricing-desc {
  font-size: 0.875rem;
  color: var(--muted);
  margin-bottom: 28px;
  line-height: 1.55;
}

.pricing-divider {
  height: 1px;
  background: var(--line);
  margin-bottom: 24px;
}

.pricing-features {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 32px;
  flex: 1;
}

.pricing-feature {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 0.875rem;
  color: var(--muted);
}

.pricing-check {
  color: var(--green);
  font-size: 0.85rem;
  flex-shrink: 0;
  margin-top: 1px;
}

.pricing-check.muted {
  color: var(--faint);
}

.pricing-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 13px 20px;
  border-radius: 999px;
  font-size: 0.9rem;
  font-weight: 600;
  border: 1px solid var(--line);
  background: transparent;
  color: var(--ink);
  transition: background 0.15s, border-color 0.15s, transform 0.1s;
}

.pricing-btn:hover {
  background: var(--surface-2);
  border-color: var(--faint);
  transform: translateY(-1px);
}

.pricing-btn.primary {
  background: var(--green);
  color: #080a0e;
  border-color: transparent;
}

.pricing-btn.primary:hover {
  background: var(--green-dim);
}

/* ── FAQ ──────────────────────────────────────────── */
.faq-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

@media (max-width: 700px) {
  .faq-grid {
    grid-template-columns: 1fr;
  }
}

.faq-item {
  padding: 28px;
  border-radius: var(--radius-lg);
  background: var(--surface);
  border: 1px solid var(--line);
}

.faq-q {
  font-size: 1rem;
  font-weight: 600;
  color: var(--ink);
  margin-bottom: 10px;
}

.faq-a {
  font-size: 0.875rem;
  line-height: 1.7;
  color: var(--muted);
}

.faq-a code {
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.82rem;
  padding: 1px 6px;
  border-radius: 4px;
  background: var(--surface-3);
  color: var(--green);
}

/* ── Banner ───────────────────────────────────────── */
.banner {
  border-radius: var(--radius-xl);
  background: linear-gradient(135deg, rgba(0,229,160,0.08) 0%, rgba(155,135,245,0.08) 100%);
  border: 1px solid rgba(0,229,160,0.15);
  padding: 64px 48px;
  text-align: center;
  margin: 0 0 96px;
}

.banner h2 {
  font-size: clamp(1.9rem, 4vw, 2.8rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  margin-bottom: 16px;
}

.banner p {
  font-size: 1.05rem;
  color: var(--muted);
  max-width: 48ch;
  margin: 0 auto 36px;
  line-height: 1.7;
}

.banner-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  flex-wrap: wrap;
}

/* ── Footer ───────────────────────────────────────── */
.footer {
  border-top: 1px solid var(--line);
  padding: 40px 0;
}

.footer-inner {
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
}

.footer-brand {
  font-size: 0.9rem;
  font-weight: 600;
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
}

.footer-brand-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--green), var(--purple));
}

.footer-links {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.footer-links a {
  font-size: 0.85rem;
  color: var(--muted);
  transition: color 0.15s;
}

.footer-links a:hover {
  color: var(--ink);
}

.footer-copy {
  font-size: 0.82rem;
  color: var(--faint);
  width: 100%;
}

/* ── Copied animation ─────────────────────────────── */
.copied-text {
  color: var(--green) !important;
}
